import { useMemo } from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { VISIT_STEPS } from '@/lib/constants'

/**
 * Sticky horizontal stepper with progress.
 * Includes a desktop view with all steps and a compact mobile view (current + progress bar).
 */
export function VisitStepper({ activeIndex, onJumpTo, completedSet, steps }) {
  const stepList = steps || VISIT_STEPS
  const total = stepList.length
  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / total) * 100),
    [activeIndex, total],
  )

  return (
    <div className="sticky top-16 z-10 -mx-4 sm:-mx-6 lg:-mx-8 border-b border-ink-100 bg-cream/90 backdrop-blur-md">
      <div className="px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
        {/* Mobile */}
        <div className="flex items-center justify-between gap-3 sm:hidden">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-700 text-cream text-sm font-bold shadow-soft">
              {activeIndex + 1}
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-500">
                Step {activeIndex + 1} of {total}
              </p>
              <p className="text-sm font-bold text-ink-900">
                {stepList[activeIndex]?.label}
              </p>
            </div>
          </div>
          <div className="text-[10px] font-bold text-primary-700">{progressPct}%</div>
        </div>
        <div className="mt-2 h-1 overflow-hidden rounded-full bg-ink-100 sm:hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-primary-500 to-primary-700 transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>

        {/* Desktop / tablet */}
        <div className="hidden sm:flex items-center gap-1.5 overflow-x-auto no-scrollbar pb-1">
          {stepList.map((step, i) => {
            const isActive = i === activeIndex
            const isComplete = completedSet?.has(i) && !isActive
            return (
              <button
                key={step.key}
                type="button"
                onClick={() => onJumpTo?.(i)}
                className={cn(
                  'group flex shrink-0 items-center gap-2 rounded-xl px-3 py-1.5 text-xs font-semibold transition-all duration-150',
                  isActive
                    ? 'bg-primary-700 text-cream shadow-soft'
                    : isComplete
                      ? 'bg-primary-50 text-primary-800 ring-1 ring-inset ring-primary-200 hover:bg-primary-100'
                      : 'text-ink-600 hover:bg-ink-100',
                )}
              >
                <span
                  className={cn(
                    'flex h-5 w-5 items-center justify-center rounded-full text-[10px]',
                    isActive
                      ? 'bg-cream/15 text-cream ring-1 ring-cream/30'
                      : isComplete
                        ? 'bg-primary-700 text-cream'
                        : 'bg-ink-200 text-ink-600',
                  )}
                >
                  {isComplete ? <Check className="h-3 w-3" strokeWidth={3} /> : i + 1}
                </span>
                <span className="hidden md:inline">{step.label}</span>
                <span className="md:hidden">{step.short}</span>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
